import { useEffect, useRef } from 'react';

import '../estilo/orbe.css';
import { desenharOrbe2D } from './orbe/fallback';
import { ENERGIA_ZERO, PARAMETROS_EM_REPOUSO, misturarParametros, parametrosDoOrbe, suavizarSinal } from './orbe/sinal';
import type { SinalDaVoz } from './orbe/sinal';
import { criarOrbeWebGL } from './orbe/webgl';
import type { OrbeWebGL } from './orbe/webgl';
import type { FaseDaVoz } from './useConversaPorVoz';

export type { FonteDoSinal, SinalDaVoz } from './orbe/sinal';

export interface OrbeDaVecchiaProps {
    fase: FaseDaVoz;
    /** Lido a cada quadro, sem re-render: o nível e as bandas do som, e de quem ele é. */
    lerSinal: () => SinalDaVoz;
    className?: string;
}

/** Lado máximo do canvas, em pixels do aparelho: o orbe é macio, e acima disso só gasta GPU. */
const LADO_MAXIMO_PX = 720;
/** Média de quadro acima disto, por um segundo, e a resolução cai um degrau (nunca volta a subir). */
const QUADRO_PESADO_MS = 22;
const QUALIDADE_MINIMA = 0.5;
const SEM_SINAL: SinalDaVoz = { fonte: 'nenhuma', ...ENERGIA_ZERO };

/**
 * `VecchiaOrb`: o orbe líquido da conversa por voz (B9, 13/09/2026). Um shader WebGL desenha a
 * massa líquida (`orbe/shader.ts`); o som chega por `lerSinal` a cada quadro e passa pela
 * suavização de `orbe/sinal.ts`. O renderizador nasce uma vez por montagem — mudar `fase` ou
 * `lerSinal` não o recria, e a camada de confirmação por cima não o toca.
 *
 * Celular fraco: se os quadros pesam, a resolução do canvas cai por degraus até a metade. Sem
 * WebGL, canvas 2D com a mesma paleta; sem canvas, camadas de CSS. O laço de quadros para com a
 * aba escondida e ao desmontar.
 */
export function OrbeDaVecchia({ fase, lerSinal, className }: OrbeDaVecchiaProps): JSX.Element {
    const tela = useRef<HTMLCanvasElement | null>(null);
    const faseRef = useRef(fase);
    const lerSinalRef = useRef(lerSinal);

    useEffect(() => {
        faseRef.current = fase;
        lerSinalRef.current = lerSinal;
    });

    useEffect(() => {
        const canvas = tela.current;
        const caixa = canvas?.parentElement;
        if (!canvas || !caixa) return undefined;
        /* O jsdom não desenha: sem esta guarda, cada teste de tela imprimiria "Not implemented". */
        if (/jsdom/i.test(navigator.userAgent)) return undefined;

        let webgl: OrbeWebGL | null = null;
        let pincel: CanvasRenderingContext2D | null = null;
        let perdido = false;
        const prepararDesenho = () => {
            const resultado = criarOrbeWebGL(canvas);
            webgl = resultado.tipo === 'pronto' ? resultado.orbe : null;
            if (resultado.tipo === 'sem-webgl') pincel = canvas.getContext('2d');
            if (!webgl && !pincel) caixa.dataset.fallback = 'css';
        };
        prepararDesenho();

        let qualidade = 1;
        const medir = () => {
            const { width, height } = caixa.getBoundingClientRect();
            const escala = Math.min(window.devicePixelRatio || 1, 2) * qualidade;
            let largura = Math.max(1, Math.round(width * escala));
            let altura = Math.max(1, Math.round(height * escala));
            const maior = Math.max(largura, altura);
            if (maior > LADO_MAXIMO_PX) {
                largura = Math.max(1, Math.round((largura * LADO_MAXIMO_PX) / maior));
                altura = Math.max(1, Math.round((altura * LADO_MAXIMO_PX) / maior));
            }
            if (canvas.width !== largura) canvas.width = largura;
            if (canvas.height !== altura) canvas.height = altura;
        };
        medir();
        const observador = typeof ResizeObserver === 'function' ? new ResizeObserver(medir) : null;
        if (observador) observador.observe(caixa);
        else window.addEventListener('resize', medir);

        const movimentoReduzido = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        let energia = ENERGIA_ZERO;
        let parametros = PARAMETROS_EM_REPOUSO;
        let tempoDoFluido = 0;
        const inicio = performance.now();
        let anterior = inicio;
        let quadro = 0;
        let somaDosQuadros = 0;
        let contagem = 0;

        const passo = (agora: number) => {
            const dtReal = Math.max(agora - anterior, 0);
            const dt = Math.min(dtReal, 100);
            anterior = agora;

            /* Qualidade adaptativa: um segundo de quadros pesados derruba a resolução um degrau. */
            somaDosQuadros += dtReal;
            contagem += 1;
            if (somaDosQuadros >= 1000) {
                if (somaDosQuadros / contagem > QUADRO_PESADO_MS && qualidade > QUALIDADE_MINIMA) {
                    qualidade = Math.max(QUALIDADE_MINIMA, qualidade * 0.8);
                    medir();
                }
                somaDosQuadros = 0;
                contagem = 0;
            }

            let sinal = SEM_SINAL;
            try {
                sinal = lerSinalRef.current();
            } catch {
                sinal = SEM_SINAL;
            }
            energia = suavizarSinal(energia, sinal, dt);
            const relogio = (agora - inicio) / 1000;
            const alvo = parametrosDoOrbe(faseRef.current, sinal.fonte, energia, relogio, Boolean(movimentoReduzido?.matches));
            parametros = misturarParametros(parametros, alvo, dt);
            /* O tempo do fluido integra a velocidade: acelerar ou frear nunca faz o desenho saltar. */
            tempoDoFluido += (dt / 1000) * parametros.velocidade;
            if (webgl && !perdido) webgl.desenhar(parametros, tempoDoFluido, relogio);
            else if (pincel) desenharOrbe2D(pincel, canvas.width, canvas.height, parametros, tempoDoFluido, relogio);
            quadro = requestAnimationFrame(passo);
        };

        const comecar = () => {
            if (quadro || document.hidden || caixa.dataset.fallback === 'css') return;
            anterior = performance.now();
            somaDosQuadros = 0;
            contagem = 0;
            quadro = requestAnimationFrame(passo);
        };
        const parar = () => {
            if (quadro) cancelAnimationFrame(quadro);
            quadro = 0;
        };
        const aoMudarVisibilidade = () => {
            if (document.hidden) parar();
            else comecar();
        };
        const aoPerderContexto = (evento: Event) => {
            /* Sem o preventDefault o navegador não devolve o contexto. */
            evento.preventDefault();
            perdido = true;
        };
        const aoRecuperarContexto = () => {
            prepararDesenho();
            perdido = false;
            comecar();
        };

        document.addEventListener('visibilitychange', aoMudarVisibilidade);
        canvas.addEventListener('webglcontextlost', aoPerderContexto);
        canvas.addEventListener('webglcontextrestored', aoRecuperarContexto);
        comecar();

        return () => {
            parar();
            document.removeEventListener('visibilitychange', aoMudarVisibilidade);
            canvas.removeEventListener('webglcontextlost', aoPerderContexto);
            canvas.removeEventListener('webglcontextrestored', aoRecuperarContexto);
            if (observador) observador.disconnect();
            else window.removeEventListener('resize', medir);
            /* Solta só os recursos: perder o contexto de propósito quebraria a remontagem do StrictMode. */
            (webgl as OrbeWebGL | null)?.destruir();
        };
    }, []);

    return (
        <div className={className ? `brain-orbe ${className}` : 'brain-orbe'} data-fase={fase}>
            <canvas ref={tela} className="brain-orbe__tela" aria-hidden="true" />
        </div>
    );
}
